<template>
  <div :data-widget-title="content.title.text">
    <SliceTitle class="mb-12 title lg:text-center" :value="content.title" />

    <div class="grid lg:grid-cols-2 gap-8 my-14">
      <RatesTable
        :heading="titleFromTo"
        :rates="ratesFromTo"
        :currency-from="content.currencyFrom.iso"
        :currency-to="content.currencyTo.iso"
        :updated-ago="updatedAgo"
      />
      <RatesTable
        :heading="titleToFrom"
        :rates="ratesToFrom"
        :currency-from="content.currencyTo.iso"
        :currency-to="content.currencyFrom.iso"
        :updated-ago="updatedAgo"
      />
    </div>

    <h3 class="mb-5 title lg:text-center">
      {{
        $t('about_exchange_rates.profile_title', {
          currencyFromIso: content.currencyFrom.iso,
          currencyToIso: content.currencyTo.iso,
        })
      }}
    </h3>
    <ul class="grid sm:grid-cols-2 sm:gap-8">
      <li
        v-for="(currency, index) in [content.currencyFrom, content.currencyTo]"
        :key="index"
        class="bg-white shadow-2 rounded-12 my-2 p-6"
      >
        <h4 class="subtitle2 mb-4 pb-4 border-b border-gray-300">
          <span class="flag rounded-4" :class="`flag-${currency.iso.toLowerCase()}`" />
          {{ $t('about_exchange_rates.about_the') }} {{ currency.labels.singular }}
        </h4>
        <dl v-if="currency.labels.singular">
          <dt>{{ $t('about_exchange_rates.name') }}:</dt>
          <dd>{{ currency.labels.singular }}</dd>
        </dl>
        <dl v-if="currency.symbol">
          <dt>{{ $t('about_exchange_rates.symbol') }}:</dt>
          <dd>{{ currency.symbol }}</dd>
        </dl>
        <dl v-if="currency.minorUnit">
          <dt>{{ $t('about_exchange_rates.minor_unit') }}:</dt>
          <dd>{{ currency.minorUnit.unit }} {{ currency.minorUnit.label }}</dd>
        </dl>
        <dl v-if="currency.centralBank">
          <dt>{{ $t('about_exchange_rates.central_bank') }}:</dt>
          <dd class="underline">
            <a :href="currency.centralBank.url" target="_blank" rel="noopener">
              {{ currency.centralBank.name }}</a
            >
          </dd>
        </dl>
        <dl v-if="currency.countries">
          <dt>{{ $t('about_exchange_rates.country', { count: currency.countries.length }) }}:</dt>
          <dd class="underline">
            {{ currency.countries.map((c) => c?.labels.label).join(', ') }}
          </dd>
        </dl>
        <dl v-if="currency.rankMostTraded">
          <dt>{{ $t('about_exchange_rates.most_traded') }}:</dt>
          <dd>#{{ currency.rankMostTraded }}</dd>
        </dl>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import type { GQLAboutExchangeRatesSlice } from './generated/about-exchange-rates'
import RatesTable from './rates-table.vue'

const props = defineProps<{ content: GQLAboutExchangeRatesSlice }>()

const { $t } = useNuxtApp()

const titleFromTo = computed(() => {
  return $t('about_exchange_rates.conversion_rates_currencyA_currencyB', {
    currencyA: props.content.currencyFrom.iso,
    currencyB: props.content.currencyTo.iso,
  })
})
const liveRateFromTo = useLiveExchangeRate(
  props.content.currencyFrom.iso,
  props.content.currencyTo.iso,
)
const ratesFromTo = computed(() => {
  if (!liveRateFromTo.value) {
    return props.content.conversionRates.ratesFromTo
  }
  return props.content.conversionRates.ratesFromTo.map(([amount]) => [
    amount,
    amount * (liveRateFromTo.value ?? 0),
  ])
})

const titleToFrom = computed(() => {
  return $t('about_exchange_rates.conversion_rates_currencyA_currencyB', {
    currencyA: props.content.currencyTo.iso,
    currencyB: props.content.currencyFrom.iso,
  })
})
const liveRateToFrom = useLiveExchangeRate(
  props.content.currencyTo.iso,
  props.content.currencyFrom.iso,
)
const ratesToFrom = computed(() => {
  if (!liveRateToFrom.value) {
    return props.content.conversionRates.ratesToFrom
  }
  return props.content.conversionRates.ratesToFrom.map(([amount]) => [
    amount,
    amount * (liveRateToFrom.value ?? 0),
  ])
})

const updatedAgo = computed(() => {
  if (liveRateFromTo.value && liveRateToFrom.value) {
    return Date.now() - 61000 // 1 minute ago
  }
  return props.content.conversionRates.updatedAgoTimestamp * 1000
})
</script>

<style scoped>
dl {
  @apply py-1;
}

dt {
  @apply font-medium inline;
}

dd {
  @apply font-bold inline;
}
</style>
